import { useState, useEffect } from 'react'
import styles from './AutomatedReportsSection.module.css'
import apiClient from '../utils/api'
import { moment } from '../utils/dateUtils'
import UpgradePromptModal from './modals/UpgradePromptModal'

export default function AutomatedReportsSection() {
    const [automatedReports, setAutomatedReports] = useState([])
    const [savedFilters, setSavedFilters] = useState([])
    const [workspaceUsers, setWorkspaceUsers] = useState([])
    const [loading, setLoading] = useState(true)
    const [showCreateModal, setShowCreateModal] = useState(false)
    const [editingReport, setEditingReport] = useState(null)
    const [error, setError] = useState(null)
    const [successMessage, setSuccessMessage] = useState('')
    const [showUpgradeModal, setShowUpgradeModal] = useState(false)
    const [upgradeModalData, setUpgradeModalData] = useState(null)

    // Load data on component mount
    useEffect(() => {
        loadData()
    }, [])

    // Helper to handle feature-blocked errors
    const handleFeatureBlocked = (err) => {
        if (err.response?.status === 403 && err.response?.data) {
            setUpgradeModalData(err.response.data)
            setShowUpgradeModal(true)
            return true
        }
        return false
    }

    const loadData = async () => {
        try {
            setLoading(true)

            const [reportsResponse, filtersResponse, usersResponse] = await Promise.all([
                apiClient.getAutomatedReports(),
                apiClient.getSavedFilters(),
                apiClient.getWorkspaceUsers()
            ])

            setAutomatedReports(reportsResponse.automated_reports || [])
            setSavedFilters(Array.isArray(filtersResponse) ? filtersResponse : (filtersResponse.saved_filters || []))
            
            // Filter to show only admin users
            const users = usersResponse.teammates || []
            const adminUsers = users.filter(user => user.role === 'admin' && user.status === 'active')
            setWorkspaceUsers(adminUsers)
        } catch (err) {
            console.error('Failed to load automated reports:', err)
            if (!handleFeatureBlocked(err)) {
                setError(`Failed to load automated reports: ${err.message}`)
            }
        } finally {
            setLoading(false)
        }
    }

    const handleCreateReport = () => {
        setEditingReport(null)
        setShowCreateModal(true)
    }

    const handleEditReport = (report) => {
        setEditingReport(report)
        setShowCreateModal(true)
    }

    const handleDeleteReport = async (reportId) => {
        if (!confirm('Are you sure you want to delete this automated report?')) return

        try {
            await apiClient.deleteAutomatedReport(reportId)
            
            setAutomatedReports(prev => prev.filter(r => r.id !== reportId))
            setSuccessMessage('Automated report deleted successfully')
            setTimeout(() => setSuccessMessage(''), 3000)
        } catch (err) {
            console.error('Failed to delete automated report:', err)
            if (!handleFeatureBlocked(err)) {
                setError('Failed to delete automated report')
            }
        }
    }

    const handleToggleActive = async (report) => {
        try {
            const response = await apiClient.toggleAutomatedReport(report.id)
            
            setAutomatedReports(prev => 
                prev.map(r => r.id === report.id ? response.automated_report : r)
            )
            setSuccessMessage(`Automated report ${response.automated_report.is_active ? 'activated' : 'deactivated'} successfully`)
            setTimeout(() => setSuccessMessage(''), 3000)
        } catch (err) {
            console.error('Failed to toggle automated report:', err)
            if (!handleFeatureBlocked(err)) {
                setError('Failed to update automated report')
            }
        }
    }

    const handleSaveReport = async (reportData) => {
        try {
            let response
            if (editingReport) {
                response = await apiClient.updateAutomatedReport(editingReport.id, reportData)
            } else {
                response = await apiClient.createAutomatedReport(reportData)
            }
            
            if (editingReport) {
                setAutomatedReports(prev => 
                    prev.map(r => r.id === editingReport.id ? response.automated_report : r)
                )
            } else {
                setAutomatedReports(prev => [...prev, response.automated_report])
            }
            
            setShowCreateModal(false)
            setSuccessMessage(response.message || 'Automated report saved successfully')
            setTimeout(() => setSuccessMessage(''), 3000)
        } catch (err) {
            console.error('Failed to save automated report:', err)
            if (!handleFeatureBlocked(err)) {
                setError('Failed to save automated report')
            }
        }
    }

    const formatSchedule = (report) => {
        const scheduleType = report.email_schedule_type
        const scheduleValue = report.email_schedule_value || {}

        // Get time display - prefer custom_time over hour
        const getTimeDisplay = () => {
            if (scheduleValue.custom_time) {
                return scheduleValue.custom_time
            }
            return `${String(scheduleValue.hour || 9).padStart(2, '0')}:00`
        }

        switch (scheduleType) {
            case 'daily':
                return `Daily at ${getTimeDisplay()}`
            case 'weekly':
                const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
                const dayName = days[scheduleValue.day_of_week || 1]
                return `Weekly on ${dayName} at ${getTimeDisplay()}`
            case 'monthly':
                return `Monthly on day ${scheduleValue.day_of_month || 1} at ${getTimeDisplay()}`
            case 'custom':
                return `Every ${scheduleValue.interval_days || 1} days at ${getTimeDisplay()}`
            default:
                return 'Not configured'
        }
    }

    const formatTimeFrame = (timeFrameType) => {
        const labels = {
            'today': 'Today',
            'yesterday': 'Yesterday',
            'this_week': 'This Week',
            'last_week': 'Last Week',
            'this_month': 'This Month',
            'last_month': 'Last Month',
            'this_year': 'This Year',
            'last_year': 'Last Year',
            'month_to_date': 'Month to Date',
            'year_to_date': 'Year to Date',
            'last_7_days': 'Last 7 Days',
            'last_30_days': 'Last 30 Days',
            'custom_range': 'Custom Range'
        }
        return labels[timeFrameType] || timeFrameType
    }

    if (loading) {
        return (
            <div className={styles.container}>
                <div className={styles.loading}>Loading automated reports...</div>
            </div>
        )
    }


    return (
        <div className={styles.container}>
            {/* Header */}
            <div className={styles.header}>
                <div className={styles.headerLeft}>
                    <h2>Automated Reports</h2>
                    <p>Schedule reports to be automatically generated and emailed on a recurring basis</p>
                </div>
                <button 
                    className={styles.createButton}
                    onClick={handleCreateReport}
                >
                    + Create Automated Report
                </button>
            </div>

            {/* Messages */}
            {successMessage && (
                <div className={styles.successMessage}>
                    {successMessage}
                </div>
            )}
            {error && (
                <div className={styles.errorMessage}>
                    {error}
                </div>
            )}

            {/* Reports List */}
            <div className={styles.reportsGrid}>
                {automatedReports.length === 0 ? (
                    <div className={styles.emptyState}>
                        <h3>No Automated Reports Yet</h3>
                        <p>Create your first automated report to get started with scheduled report delivery.</p>
                        <button 
                            className={styles.createButton}
                            onClick={handleCreateReport}
                        >
                            + Create Your First Automated Report
                        </button>
                    </div>
                ) : (
                    automatedReports.map(report => (
                        <div key={report.id} className={styles.reportCard}>
                            <div className={styles.reportHeader}>
                                <h3>{report.name}</h3>
                                <div className={styles.reportActions}>
                                    <button
                                        className={`${styles.toggleButton} ${report.is_active ? styles.active : styles.inactive}`}
                                        onClick={() => handleToggleActive(report)}
                                        title={report.is_active ? 'Deactivate' : 'Activate'}
                                    >
                                        {report.is_active ? 'Active' : 'Inactive'}
                                    </button>
                                    <button
                                        className={styles.editButton}
                                        onClick={() => handleEditReport(report)}
                                        title="Edit Report"
                                    >
                                        ✏️
                                    </button>
                                    <button
                                        className={styles.deleteButton}
                                        onClick={() => handleDeleteReport(report.id)}
                                        title="Delete Report"
                                    >
                                        🗑️
                                    </button>
                                </div>
                            </div>

                            <div className={styles.reportDetails}>
                                <div className={styles.detailItem}>
                                    <label>Saved Filter:</label>
                                    <span>{report.saved_filter.name}</span>
                                </div>
                                <div className={styles.detailItem}>
                                    <label>Time Frame:</label>
                                    <span>{formatTimeFrame(report.time_frame_type)}</span>
                                </div>
                                <div className={styles.detailItem}>
                                    <label>Schedule:</label>
                                    <span>{formatSchedule(report)}</span>
                                </div>
                                <div className={styles.detailItem}>
                                    <label>Recipients:</label>
                                    <span>{report.recipient_emails?.length || 0} recipient(s)</span>
                                </div>
                                {report.last_sent_at && (
                                    <div className={styles.detailItem}>
                                        <label>Last Sent:</label>
                                        <span>{moment(report.last_sent_at).format('MMM D, YYYY h:mm A')}</span>
                                    </div>
                                )}
                                {report.next_send_at && (
                                    <div className={styles.detailItem}>
                                        <label>Next Send:</label>
                                        <span>{moment(report.next_send_at).format('MMM D, YYYY h:mm A')}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))
                )}
            </div>

            {/* Create/Edit Modal */}
            {showCreateModal && (
                <AutomatedReportModal
                    report={editingReport}
                    savedFilters={savedFilters}
                    workspaceUsers={workspaceUsers}
                    onSave={handleSaveReport}
                    onCancel={() => setShowCreateModal(false)}
                />
            )}

            {/* Upgrade Prompt Modal */}
            {showUpgradeModal && upgradeModalData && (
                <UpgradePromptModal
                    isOpen={showUpgradeModal}
                    onClose={() => setShowUpgradeModal(false)}
                    featureName={upgradeModalData.feature || 'automated_reports'}
                    currentPlan={upgradeModalData.current_plan || 'free'}
                    requiredPlan={upgradeModalData.required_plan || 'pro'}
                    upgradeInfo={upgradeModalData}
                />
            )}
        </div>
    )
}

// Modal Component for Creating/Editing Automated Reports
function AutomatedReportModal({ report, savedFilters, workspaceUsers, onSave, onCancel }) {
    const [formData, setFormData] = useState({
        name: report?.name || '',
        saved_filter_id: report?.saved_filter?.id || '',
        time_frame_type: report?.time_frame_type || 'this_week',
        time_frame_value: report?.time_frame_value || {},
        email_schedule_type: report?.email_schedule_type || 'weekly',
        email_schedule_value: report?.email_schedule_value || { day_of_week: 1, hour: 9 },
        recipient_emails: report?.recipient_emails || [],
        selected_user_ids: [], // Track selected user IDs for UI
        is_active: report?.is_active !== undefined ? report.is_active : true
    })
    
    const [timeFrameOptions, setTimeFrameOptions] = useState([])
    const [scheduleOptions, setScheduleOptions] = useState([])
    const [loading, setLoading] = useState(false)
    const [preview, setPreview] = useState(null)

    useEffect(() => {
        loadOptions()
    }, [])

    // Initialize selected user IDs when editing a report and workspaceUsers are loaded
    useEffect(() => {
        if (report && workspaceUsers.length > 0 && formData.recipient_emails.length > 0) {
            const selectedIds = workspaceUsers
                .filter(user => formData.recipient_emails.includes(user.email))
                .map(user => user.id)
            setFormData(prev => ({
                ...prev,
                selected_user_ids: selectedIds
            }))
        }
    }, [report, workspaceUsers, formData.recipient_emails.length])

    const loadOptions = async () => {
        try {
            const [timeFrameResponse, scheduleResponse] = await Promise.all([
                apiClient.getAutomatedReportsTimeFrameOptions(),
                apiClient.getAutomatedReportsScheduleOptions()
            ])

            setTimeFrameOptions(timeFrameResponse.time_frame_options || [])
            setScheduleOptions(scheduleResponse.schedule_options || [])
        } catch (err) {
            console.error('Failed to load options:', err)
        }
    }

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }))
    }

    const handleScheduleValueChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            email_schedule_value: {
                ...prev.email_schedule_value,
                [field]: value
            }
        }))
    }

    const handleUserToggle = (userId) => {
        const user = workspaceUsers.find(u => u.id === userId)
        if (!user) return

        let newSelectedIds
        let newEmails

        if (formData.selected_user_ids.includes(userId)) {
            // Remove user
            newSelectedIds = formData.selected_user_ids.filter(id => id !== userId)
            newEmails = formData.recipient_emails.filter(email => email !== user.email)
        } else {
            // Add user
            newSelectedIds = [...formData.selected_user_ids, userId]
            newEmails = [...formData.recipient_emails, user.email]
        }

        setFormData(prev => ({
            ...prev,
            selected_user_ids: newSelectedIds,
            recipient_emails: newEmails
        }))
    }

    const handlePreview = async () => {
        if (!formData.saved_filter_id || !formData.time_frame_type) return

        try {
            setLoading(true)
            const response = await apiClient.previewAutomatedReport({
                saved_filter_id: formData.saved_filter_id,
                time_frame_type: formData.time_frame_type,
                time_frame_value: formData.time_frame_value
            })
            setPreview(response.preview)
        } catch (err) {
            console.error('Failed to generate preview:', err)
        } finally {
            setLoading(false)
        }
    }

    const handleSubmit = (e) => {
        e.preventDefault()
        
        if (formData.recipient_emails.length === 0) {
            alert('Please select at least one user to receive the reports')
            return
        }

        onSave(formData)
    }

    const selectedScheduleOption = scheduleOptions.find(opt => opt.value === formData.email_schedule_type)

    return (
        <div className={styles.modalOverlay}>
            <div className={styles.modal}>
                <div className={styles.modalHeader}>
                    <h3>{report ? 'Edit' : 'Create'} Automated Report</h3>
                    <button className={styles.closeButton} onClick={onCancel}>×</button>
                </div>

                <form onSubmit={handleSubmit} className={styles.modalContent}>
                    {/* Basic Information */}
                    <div className={styles.formGroup}>
                        <label>Report Name *</label>
                        <input
                            type="text"
                            value={formData.name}
                            onChange={(e) => handleInputChange('name', e.target.value)}
                            placeholder="Enter report name"
                            required
                        />
                    </div>

                    <div className={styles.formGroup}>
                        <label>Saved Filter *</label>
                        <select
                            value={formData.saved_filter_id}
                            onChange={(e) => handleInputChange('saved_filter_id', e.target.value)}
                            required
                        >
                            <option value="">Select a saved filter</option>
                            {savedFilters.map(filter => (
                                <option key={filter.id} value={filter.id}>{filter.name}</option>
                            ))}
                        </select>
                    </div>

                    {/* Time Frame */}
                    <div className={styles.formGroup}>
                        <label>Time Frame *</label>
                        <select
                            value={formData.time_frame_type}
                            onChange={(e) => handleInputChange('time_frame_type', e.target.value)}
                            required
                        >
                            {timeFrameOptions.map(option => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                        <small className={styles.helpText}>
                            {timeFrameOptions.find(opt => opt.value === formData.time_frame_type)?.description}
                        </small>
                    </div>

                    {/* Email Schedule */}
                    <div className={styles.formGroup}>
                        <label>Email Schedule *</label>
                        <select
                            value={formData.email_schedule_type}
                            onChange={(e) => handleInputChange('email_schedule_type', e.target.value)}
                            required
                        >
                            {scheduleOptions.map(option => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                        <small className={styles.helpText}>
                            {selectedScheduleOption?.description}
                        </small>
                    </div>

                    {/* Schedule Configuration */}
                    {selectedScheduleOption?.config_fields && (
                        <div className={styles.scheduleConfig}>
                            {selectedScheduleOption.config_fields.map(field => (
                                <div key={field.name} className={styles.formGroup}>
                                    <label>{field.label}</label>
                                    {field.description && (
                                        <small className={styles.fieldDescription}>{field.description}</small>
                                    )}
                                    {field.type === 'select' ? (
                                        <select
                                            value={formData.email_schedule_value[field.name] || ''}
                                            onChange={(e) => handleScheduleValueChange(field.name, parseInt(e.target.value))}
                                        >
                                            {field.options.map(opt => (
                                                <option key={opt.value} value={opt.value}>
                                                    {opt.label}
                                                </option>
                                            ))}
                                        </select>
                                    ) : field.type === 'time' ? (
                                        <input
                                            type="time"
                                            value={formData.email_schedule_value[field.name] || ''}
                                            onChange={(e) => handleScheduleValueChange(field.name, e.target.value)}
                                            step="300"
                                        />
                                    ) : (
                                        <input
                                            type={field.type}
                                            value={formData.email_schedule_value[field.name] || ''}
                                            onChange={(e) => handleScheduleValueChange(field.name, parseInt(e.target.value))}
                                            min={field.min}
                                            max={field.max}
                                        />
                                    )}
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Recipients */}
                    <div className={styles.formGroup}>
                        <label>Recipients (Admin Users) *</label>
                        <div className={styles.usersList}>
                            {workspaceUsers.length > 0 ? (
                                workspaceUsers.map(user => (
                                    <div key={user.id} className={styles.userItem}>
                                        <label className={`${styles.userLabel} ${formData.selected_user_ids.includes(user.id) ? styles.selected : ''}`}>
                                            <input
                                                type="checkbox"
                                                checked={formData.selected_user_ids.includes(user.id)}
                                                onChange={() => handleUserToggle(user.id)}
                                            />
                                            <span className={styles.userName}>
                                                {user.first_name} {user.last_name}
                                            </span>
                                            <span className={styles.userEmail}>
                                                ({user.email})
                                            </span>
                                        </label>
                                    </div>
                                ))
                            ) : (
                                <p className={styles.noUsers}>No admin users found in workspace</p>
                            )}
                        </div>
                        {formData.selected_user_ids.length > 0 && (
                            <small className={styles.selectedCount}>
                                {formData.selected_user_ids.length} user(s) selected
                            </small>
                        )}
                    </div>

                    {/* Active Toggle */}
                    <div className={styles.formGroup}>
                        <div className={styles.checkboxGroup}>
                            <input
                                type="checkbox"
                                id="is-active-checkbox"
                                checked={formData.is_active}
                                onChange={(e) => handleInputChange('is_active', e.target.checked)}
                            />
                            <label htmlFor="is-active-checkbox">
                                Active (report will be sent according to schedule)
                            </label>
                        </div>
                    </div>

                    {/* Preview */}
                    {formData.saved_filter_id && formData.time_frame_type && (
                        <div className={styles.previewSection}>
                            <button
                                type="button"
                                className={styles.previewButton}
                                onClick={handlePreview}
                                disabled={loading}
                            >
                                {loading ? 'Generating Preview...' : 'Preview Report'}
                            </button>
                            
                            {preview && (
                                <div className={styles.preview}>
                                    <h4>Preview</h4>
                                    <p><strong>Date Range:</strong> {preview.date_range.start_date} to {preview.date_range.end_date}</p>
                                    <p><strong>Total Entries:</strong> {preview.total_entries}</p>
                                    <p><strong>Total Time:</strong> {preview.summary.total_duration_formatted}</p>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Form Actions */}
                    <div className={styles.formActions}>
                        <button type="button" className={styles.cancelButton} onClick={onCancel}>
                            Cancel
                        </button>
                        <button type="submit" className={styles.saveButton}>
                            {report ? 'Update' : 'Create'} Report
                        </button>
                    </div>
                </form>
            </div>
        </div>
    )
}